import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { ArrowRight, ArrowDown, Palette, Globe, Video, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VideoSlider from "@/components/VideoSlider";
import ServiceCard from "@/components/ServiceCard";
import ScrollReveal from "@/components/ScrollReveal";
import ClientLogos from "@/components/ClientLogos";
import SEO from "@/components/SEO";
import PageBackground from "@/components/PageBackground";
import porscheCupCtaBg from "@/assets/porschecup-cta-bg.jpg.asset.json";

import youmove30 from "@/assets/YouMove_Kurse_4FACEMEDIA_30.jpg.asset.json";
import angacomZcn1 from "@/assets/angacom-zcn-1.jpg.asset.json";
import goodyear0 from "@/assets/goodyear-nuerburgring-0.jpg.asset.json";
import porscheV2_1 from "@/assets/porsche-cup-v2-1.jpg.asset.json";
import grasbahnrennen1Asset from "@/assets/grasbahnrennen-1.jpg.asset.json";
const grasbahnrennen1 = grasbahnrennen1Asset.url;
import sparkassenstarsLogoAsset from "@/assets/sparkassenstars-logo.jpg.asset.json";
const sparkassenstarsLogo = sparkassenstarsLogoAsset.url;
import limitlessHeroAsset from "@/assets/limitless-lh-hero.jpg.asset.json";
const limitlessHero = limitlessHeroAsset.url;
import ruth1Asset from "@/assets/ruth-schoko-1.jpg.asset.json";
const ruth1 = ruth1Asset.url;
import papageienpark1Asset from "@/assets/papageienpark-1.jpg.asset.json";
const papageienpark1 = papageienpark1Asset.url;
import tusHarpen1Asset from "@/assets/tus-harpen-1.jpg.asset.json";
const tusHarpen1 = tusHarpen1Asset.url;
import grasbahnrennen2Asset from "@/assets/grasbahnrennen-2.jpg.asset.json";
const grasbahnrennen2 = grasbahnrennen2Asset.url;
import papageienparkFoto1Asset from "@/assets/papageienpark-foto-1.jpg.asset.json";
const papageienparkFoto1 = papageienparkFoto1Asset.url;
import missionNight1Asset from "@/assets/mission-night-1.jpg.asset.json";
const missionNight1 = missionNight1Asset.url;
import cronex7 from "@/assets/cronex-7.jpg.asset.json";

const ctaBgImages = [porscheCupCtaBg.url];

const aktuelles = [
  { title: "Cronex Automobile Wuppertal", category: "Foto", image: cronex7.url, filter: "Foto" },
  { title: "YouMove Bochum", category: "Foto & Social Media", image: youmove30.url, filter: "Foto" },
  { title: "ZCN Fiber – ANGACOM Messe", category: "Foto", image: angacomZcn1.url, filter: "Foto" },
  { title: "Goodyear Racing Days Nürburgring", category: "Foto", image: goodyear0.url, filter: "Foto" },
  { title: "Mission Night", category: "Foto", image: missionNight1, filter: "Foto" },
  { title: "Grasbahnrennen Lüdinghausen 2026", category: "Video", image: grasbahnrennen1, filter: "Video" },
  { title: "VfL Sparkassenstars Bochum", category: "Foto", image: sparkassenstarsLogo, filter: "Foto" },
  { title: "Limitless LH Webseite", category: "Web", image: limitlessHero, filter: "Web" },
  { title: "Ruth Schokoladenwerk", category: "Foto", image: ruth1, filter: "Foto" },
  { title: "Porsche Carrera Cup Italia Imola", category: "Foto", image: porscheV2_1.url, filter: "Foto" },
  { title: "Papageienpark Bochum", category: "Foto", image: papageienpark1, filter: "Foto" },
  { title: "TuS Harpen", category: "Foto", image: tusHarpen1, filter: "Foto" },
  { title: "Grasbahnrennen Lüdinghausen 2025", category: "Foto", image: grasbahnrennen2, filter: "Foto" },
  { title: "Papageienpark Social Media", category: "Social Media", image: papageienparkFoto1, filter: "Social Media" },
  { title: "ADAC Racing Weekend Nürburgring", category: "Video", image: "https://img.youtube.com/vi/-E-CTjosBDM/maxresdefault.jpg", filter: "Video" },
];


const services = [
  {
    icon: Video,
    title: "Motorsport & Sport",
    description: "Cinematic Race Coverage, On-Track Storytelling & Athleten-Content. Vom Boxengasse-Insight bis zum Renn-Aftermovie.",
    link: "/leistungen",
  },
  {
    icon: Share2,
    title: "Eventbegleitung",
    description: "Eventfilm, Live-Content & Aftermovies, die Atmosphäre, Adrenalin & echte Momente digital erlebbar machen.",
    link: "/leistungen",
  },
  {
    icon: Palette,
    title: "Branding & Identity",
    description: "Logo, Markenwelt & Geschäftsausstattung mit Premium-Anspruch. Marken, die im Gedächtnis bleiben.",
    link: "/leistungen",
  },
  {
    icon: Globe,
    title: "Web & Social",
    description: "Performante Webseiten & Social-Media-Storytelling. Aus Bochum, deutschlandweit im Einsatz.",
    link: "/leistungen",
  },
];

// Triple the list so the carousel can loop seamlessly in both directions
const aktuellesLoop = [...aktuelles, ...aktuelles, ...aktuelles];

const Index = () => {
  const [ctaBgIndex, setCtaBgIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isAdjustingRef = useRef(false);

  // Position scroll to the middle copy on mount so we can loop both ways
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const setStart = () => {
      isAdjustingRef.current = true;
      el.scrollLeft = el.scrollWidth / 3;
      requestAnimationFrame(() => {
        isAdjustingRef.current = false;
      });
    };
    setStart();
    window.addEventListener("resize", setStart);
    return () => window.removeEventListener("resize", setStart);
  }, []);

  // Seamless loop: when user scrolls into 1st or 3rd copy, jump back to middle copy
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => {
      if (isAdjustingRef.current) return;
      const third = el.scrollWidth / 3;
      if (el.scrollLeft < third * 0.5) {
        isAdjustingRef.current = true;
        el.scrollLeft += third;
        requestAnimationFrame(() => (isAdjustingRef.current = false));
      } else if (el.scrollLeft > third * 1.5) {
        isAdjustingRef.current = true;
        el.scrollLeft -= third;
        requestAnimationFrame(() => (isAdjustingRef.current = false));
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollBy = (dir: number) => {
    const el = carouselRef.current;
    if (!el) return;
    // Scroll by one card width (1/4 of viewport on lg)
    const cardWidth = el.clientWidth / 4;
    el.scrollBy({ left: dir * cardWidth, behavior: "smooth" });
  };

  useEffect(() => {
    const id = setInterval(() => {
      setCtaBgIndex((i) => (i + 1) % ctaBgImages.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (



    <div className="min-h-screen bg-background relative overflow-hidden">
      <PageBackground />
      <div className="relative z-10">
      <SEO title="4Facemedia – Motorsport, Sport & Eventfilm Agentur Bochum" description="Cinematic Medienproduktion für Motorsport, Sport & Events aus Bochum – deutschlandweit im Einsatz." path="/" />
      <Header />

      {/* Video Slider — fullscreen cinematic */}
      <VideoSlider />

      {/* Cinematic Hero — fullscreen, immersive */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-[15%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" style={{ animation: "float 10s ease-in-out infinite" }} />
          <div className="absolute bottom-1/4 left-[5%] w-[600px] h-[600px] rounded-full bg-glow/4 blur-[150px]" style={{ animation: "float 12s ease-in-out infinite 3s" }} />
        </div>

        {/* Decorative lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-[20%] w-[1px] h-full bg-gradient-to-b from-transparent via-foreground/5 to-transparent" />
          <div className="absolute top-0 left-[80%] w-[1px] h-full bg-gradient-to-b from-transparent via-foreground/5 to-transparent" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <div className="overflow-hidden mb-8">
              <span className="inline-block text-sm tracking-[0.3em] uppercase text-primary/80 font-medium animate-fade-up">
                Motorsport · Sport · Eventfilm · Bochum
              </span>
            </div>
            <div className="overflow-hidden">
              <h1 className="text-5xl md:text-7xl lg:text-[6.5rem] font-bold leading-[0.95] mb-8 animate-fade-up-delay-1">
                <span className="text-foreground block">Adrenalin.</span>
                <span className="text-gradient block">Atmosphäre.</span>
                <span className="text-foreground block">Digital erlebbar.</span>
              </h1>
            </div>
            <p className="text-lg md:text-xl text-foreground/85 max-w-2xl mb-12 leading-relaxed font-light animate-fade-up-delay-2">
              Wir verwandeln Motorsport, Sport & Events in cinematic Geschichten. Von der Strecke bis zum Screen – aus Bochum, deutschlandweit im Einsatz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up-delay-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground text-base px-10 h-14 rounded-full">
                <Link to="/kontakt">
                  Projekt starten <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-foreground/20 hover:bg-foreground/5 text-foreground text-base px-10 h-14 rounded-full">
                <Link to="/projekte">Showreel ansehen</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 animate-fade-up-delay-3">
          <span className="text-[10px] tracking-[0.3em] uppercase text-foreground/30">Scroll</span>
          <ArrowDown size={16} className="text-foreground/30 animate-bounce" />
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-32 relative">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="max-w-3xl mb-20">
              <span className="text-xs tracking-[0.3em] uppercase text-primary/80 font-medium">Unser Spielfeld</span>
              <h2 className="text-4xl md:text-6xl font-bold text-foreground mt-6 leading-tight">
                Von der Strecke<br />bis zum Screen.
              </h2>
              <p className="text-foreground/85 mt-6 text-lg font-light leading-relaxed max-w-xl">
                Spezialisten für Motorsport, Sport & Eventbegleitung. Branding, Web & Social runden das Bild ab – immer mit dem Anspruch, echte Momente premium zu inszenieren.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <ScrollReveal key={service.title} delay={i * 100}>
                <ServiceCard {...service} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-32 relative">
        <div className="absolute inset-0 bg-card/20" />
        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <ScrollReveal>
              <div>
                <span className="text-xs tracking-[0.3em] uppercase text-primary/80 font-medium">Über uns</span>
                <h2 className="text-4xl md:text-6xl font-bold text-foreground mt-6 mb-8 leading-tight">
                  Momente vergehen.<br />
                  <span className="text-gradient">Geschichten bleiben.</span>
                </h2>
                <p className="text-foreground/85 leading-relaxed mb-6 font-light text-lg">
                  4Facemedia ist eine Medienagentur aus Bochum mit Schwerpunkt auf Motorsport, Sport & Eventbegleitung. Wir arbeiten dort, wo es laut, schnell & emotional wird – und übersetzen das in cinematic Content, der bleibt.
                </p>
                <p className="text-foreground/85 leading-relaxed mb-10 font-light text-lg">
                  Ob Rennstrecke am Nürburgring, Grasbahn in Lüdinghausen oder Eventreihe im Ruhrgebiet: Wir liefern Bilder, Filme & Stories, die Adrenalin spürbar machen.
                </p>
                <Link
                  to="/ueber-uns"
                  className="inline-flex items-center gap-3 text-foreground text-sm font-medium tracking-wide group"
                >
                  Mehr über uns
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { number: "70+", label: "Projekte" },
                  { number: "50+", label: "Kunden" },
                  { number: "3+", label: "Jahre Erfahrung" },
                  { number: "360°", label: "Service" },
                ].map((stat) => (
                  <div key={stat.label} className="p-10 rounded-2xl bg-gradient-card border border-border/30 text-center group hover:border-primary/20 transition-all duration-500 hover:scale-105">
                    <div className="text-4xl font-bold text-gradient mb-3">{stat.number}</div>
                    <div className="text-sm text-foreground/85 font-light">{stat.label}</div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Aktuelles */}
      <section className="py-32 relative">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <span className="text-xs tracking-[0.3em] uppercase text-primary/80 font-medium">Aktuelles</span>
                <h2 className="text-4xl md:text-6xl font-bold text-foreground mt-6 leading-tight">
                  Frisch aus<br />der Produktion.
                </h2>
                <p className="text-foreground/85 mt-6 text-lg font-light leading-relaxed">
                  Unsere neuesten Projekte – einfach durchswipen.
                </p>
              </div>
              <Link
                to="/projekte"
                className="inline-flex items-center gap-3 text-foreground text-sm font-medium tracking-wide group whitespace-nowrap"
              >
                Alle Projekte
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <div className="relative">
              <button
                onClick={() => scrollBy(-1)}
                className="hidden lg:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-background/80 border border-border/40 text-foreground hover:bg-background transition-all shadow-lg"
                aria-label="Zurück"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={() => scrollBy(1)}
                className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-background/80 border border-border/40 text-foreground hover:bg-background transition-all shadow-lg"
                aria-label="Weiter"
              >
                <ChevronRight size={24} />
              </button>
              <div
                ref={carouselRef}
                className="flex gap-4 overflow-x-auto pb-6 -mx-6 px-6 scrollbar-hide"
                style={{ scrollbarWidth: "none" }}
              >
                {aktuellesLoop.map((item, idx) => (
                  <Link
                    key={`${item.title}-${idx}`}
                    to={`/projekte?filter=${item.filter}`}
                    className="group relative shrink-0 w-[70%] sm:w-[42%] md:w-[calc(30%-0.7rem)] lg:w-[calc(22%-0.78rem)] aspect-[4/5] rounded-2xl overflow-hidden border border-border/30 hover:border-primary/40 transition-all duration-500"
                  >
                    <img decoding="async"
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-primary/90 font-medium">
                        {item.category}
                      </span>
                      <h3 className="text-lg md:text-xl font-bold text-foreground mt-2 leading-tight">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>


      {/* CTA — cinematic */}

      <section className="py-40 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          {ctaBgImages.map((src, i) => (
            <img decoding="async"
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out"
              style={{ opacity: ctaBgIndex === i ? 0.35 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background/90" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/10 blur-[200px]" />
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-8 leading-tight">
              Bereit, dein nächstes<br />
              <span className="text-gradient">Highlight zu inszenieren?</span>
            </h2>
            <p className="text-foreground/85 text-lg max-w-xl mx-auto mb-12 font-light">
              Erzähl uns von deinem Event, deinem Team oder deiner Saison.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground text-base px-12 h-14 rounded-full">
              <Link to="/kontakt">
                Jetzt Kontakt aufnehmen <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      <ClientLogos />
      <Footer />
      </div>
    </div>
  );
};

export default Index;
